import React, { useEffect, useState } from 'react';
import { 
  Table, 
  Input, 
  Button, 
  Space, 
  Tag, 
  Card,
  DatePicker,
  Select,
  Row,
  Col,
  message
} from 'antd';
import { 
  SearchOutlined, 
  FilterOutlined,
  ExportOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { fetchAdmins } from '../../services/firestoreService';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { Option } = Select;

const AuditLogs = () => {
  const { getAuditLogs, logActivity } = useAdminAuth();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [actionFilter, setActionFilter] = useState('all');
  const [adminFilter, setAdminFilter] = useState('all');
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadLogs();
    loadAdmins();
    logActivity('view_audit_logs', 'Viewed audit logs page');
  }, []);

  useEffect(() => {
    filterLogs();
  }, [logs, searchText, dateRange, actionFilter, adminFilter]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const allLogs = await getAuditLogs();
      setLogs(allLogs);
    } catch {
      message.error('Failed to load audit logs from Firebase');
    } finally {
      setLoading(false);
    }
  };

  const loadAdmins = async () => {
    try {
      const storedAdmins = await fetchAdmins();
      setAdmins(storedAdmins);
    } catch {
      console.error('Failed to load admins');
    }
  };

  const filterLogs = () => {
    let filtered = [...logs];

    // Search filter
    if (searchText) {
      filtered = filtered.filter(log => 
        log.action?.toLowerCase().includes(searchText.toLowerCase()) ||
        log.details?.toLowerCase().includes(searchText.toLowerCase()) ||
        log.adminName?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Date range filter
    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      filtered = filtered.filter(log => {
        const logDate = moment(log.timestamp);
        return logDate.isBetween(start, end, 'day', '[]');
      });
    }

    // Action filter
    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action === actionFilter);
    }

    // Admin filter
    if (adminFilter !== 'all') {
      filtered = filtered.filter(log => log.adminId === adminFilter);
    }

    setFilteredLogs(filtered);
  };

  const handleExportCSV = () => {
    const csv = [
      ['Timestamp', 'Admin', 'Action', 'Details', 'IP Address'],
      ...filteredLogs.map(log => [
        new Date(log.timestamp).toLocaleString(),
        log.adminName,
        log.action,
        log.details || '',
        log.ipAddress || 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    logActivity('export_audit_logs', `Exported ${filteredLogs.length} audit logs to CSV`);
    message.success('Audit logs exported successfully');
  };

  const getActionColor = (action) => {
    const colors = {
      login: 'blue',
      logout: 'default',
      view_dashboard: 'cyan',
      view_users: 'cyan',
      view_user_details: 'cyan',
      view_audit_logs: 'cyan',
      update_user: 'orange',
      suspend_user: 'red',
      reactivate_user: 'green',
      delete_user: 'red',
      upgrade_subscription: 'gold',
      downgrade_subscription: 'orange',
      export_users: 'purple',
      export_audit_logs: 'purple',
      create_category: 'green',
      update_category: 'orange',
      delete_category: 'red'
    };
    return colors[action] || 'default';
  };

  const getActionIcon = (action) => {
    if (action.includes('view')) return '👁️';
    if (action.includes('create')) return '➕';
    if (action.includes('update') || action.includes('edit')) return '✏️';
    if (action.includes('delete')) return '🗑️';
    if (action.includes('suspend')) return '🔒';
    if (action.includes('reactivate')) return '🔓';
    if (action.includes('export')) return '📊';
    if (action.includes('login')) return '🔐';
    if (action.includes('logout')) return '🚪';
    return '📝';
  };

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => (
        <div>
          <div className="font-medium">{new Date(timestamp).toLocaleDateString()}</div>
          <div className="text-xs text-gray-500">{new Date(timestamp).toLocaleTimeString()}</div>
        </div>
      ),
      sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
      defaultSortOrder: 'descend',
      width: 150
    },
    {
      title: 'Admin',
      dataIndex: 'adminName',
      key: 'adminName',
      render: (name, record) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center text-white text-xs font-bold">
            {name?.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="font-medium">{name}</div>
            <div className="text-xs text-gray-500">{typeof record.adminId === 'object' ? record.adminId.email || 'N/A' : record.adminId}</div>
          </div>
        </div>
      ),
      width: 200
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => (
        <Tag color={getActionColor(action)} className="flex items-center gap-1 w-fit">
          <span>{getActionIcon(action)}</span>
          <span>{action.replace(/_/g, ' ').toUpperCase()}</span>
        </Tag>
      ),
      width: 200
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      render: (details) => {
        if (typeof details === 'object' && details !== null) {
          return <span className="text-gray-700">{JSON.stringify(details)}</span>;
        }
        return <span className="text-gray-700">{details || 'N/A'}</span>;
      },
      ellipsis: true
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      render: (ip) => (
        <span className="text-gray-500 text-sm font-mono">{ip || 'N/A'}</span>
      ),
      width: 150
    }
  ];

  const uniqueActions = [...new Set(logs.map(log => log.action))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Audit Logs</h1>
          <p className="text-gray-500 mt-1">Monitor all admin activities and system events</p>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadLogs}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<ExportOutlined />}
            onClick={handleExportCSV}
            className="bg-cyan-400 hover:bg-cyan-500"
          >
            Export CSV
          </Button>
        </Space>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={6}>
          <Card className="text-center">
            <div className="text-3xl font-bold text-blue-500">{logs.length}</div>
            <div className="text-gray-500 mt-1">Total Events</div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="text-center">
            <div className="text-3xl font-bold text-green-500">
              {logs.filter(log => log.timestamp > Date.now() - 24 * 60 * 60 * 1000).length}
            </div>
            <div className="text-gray-500 mt-1">Last 24 Hours</div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="text-center">
            <div className="text-3xl font-bold text-orange-500">
              {logs.filter(log => log.action.includes('update') || log.action.includes('delete')).length}
            </div>
            <div className="text-gray-500 mt-1">Modifications</div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="text-center">
            <div className="text-3xl font-bold text-cyan-500">
              {new Set(logs.map(log => log.adminId)).size}
            </div>
            <div className="text-gray-500 mt-1">Active Admins</div>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card>
        <Space size="middle" wrap className="w-full">
          <Input
            placeholder="Search logs..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
          
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            format="YYYY-MM-DD"
            placeholder={['Start Date', 'End Date']}
          />

          <Select
            value={actionFilter}
            onChange={setActionFilter}
            style={{ width: 200 }}
            placeholder="Filter by action"
          >
            <Option value="all">All Actions</Option>
            {uniqueActions.map(action => (
              <Option key={action} value={action}>
                {action.replace(/_/g, ' ').toUpperCase()}
              </Option>
            ))}
          </Select>

          <Select
            value={adminFilter}
            onChange={setAdminFilter}
            style={{ width: 200 }}
            placeholder="Filter by admin"
          >
            <Option value="all">All Admins</Option>
            {admins.map(admin => (
              <Option key={admin.email} value={admin.email}>
                {admin.name}
              </Option>
            ))}
          </Select>
        </Space>
      </Card>

      {/* Logs Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredLogs}
          rowKey={(record) => `${record.timestamp}-${record.adminId}`}
          pagination={{ 
            pageSize: 15, 
            showSizeChanger: true, 
            showTotal: (total) => `Total ${total} logs` 
          }}
          loading={loading}
          scroll={{ x: true }}
        />
      </Card>
    </div>
  );
};

export default AuditLogs;
